import React, { useEffect, useMemo, useRef, useState } from 'react';
import { onCommand } from '../lib/commandBus';
import {
  clearDrafts,
  composePullHypothesis,
  DEFAULT_PULL,
  downloadCsv,
  loadDraftCsvColumns,
  loadDraftTemplate,
  saveDraftCsvColumns,
  saveDraftTemplate,
  syncDraftsFromContacts,
} from '../lib/storage';
import Lineage from './Lineage';
import ExternalLink from './ExternalLink';
import { Chip, classificationTone, verificationTone } from './Chip';
import { DataTable, Td, TdAction, Tr } from './DataTable';

function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  maxWidth = 'max-w-md',
  titleId,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink/20" onClick={onClose} />
      <div
        role="dialog"
        aria-labelledby={titleId}
        className={`relative bg-white rounded-[6px] shadow-popover border border-paper-line w-full ${maxWidth} overflow-hidden flex flex-col max-h-[min(90vh,760px)]`}
      >
        <div className="px-5 pt-4 pb-3 border-b border-paper-line flex items-start justify-between gap-3 flex-shrink-0">
          <div className="min-w-0">
            <h2
              id={titleId}
              className="text-[15px] font-semibold tracking-tight text-ink"
            >
              {title}
            </h2>
            {subtitle ? (
              <div className="text-[12px] text-ink-faint mt-0.5">{subtitle}</div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-faint hover:text-ink p-1 flex-shrink-0"
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="w-4 h-4"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="px-5 py-4 overflow-y-auto min-h-0">{children}</div>
        {footer ? (
          <div className="px-5 py-3 border-t border-paper-line flex items-center justify-end gap-2 bg-paper-soft flex-shrink-0">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

const WILDCARDS = [
  'first_name',
  'email',
  'domain',
  'product_name',
  'tagline',
  'description',
  'votes_count',
  'ph_url',
  'website',
  'classification',
  'verification',
  'pull_hypothesis',
];

const PULL_KEYS = [
  { id: 'project', letter: 'P', label: 'Project', hint: 'Project on their to-do list' },
  { id: 'unavoidable', letter: 'U', label: 'Unavoidable', hint: 'Unavoidable right now' },
  { id: 'looking', letter: 'L', label: 'Looking', hint: 'Options they Look into' },
  { id: 'lacking', letter: 'L', label: 'Lacking', hint: 'Lacking for a critical reason' },
];

function PullHypothesisField({ pull, onChange }) {
  const values = { ...DEFAULT_PULL, ...(pull || {}) };
  const sentence = composePullHypothesis(values);
  const [focused, setFocused] = useState(false);
  const [activeSlot, setActiveSlot] = useState('project');
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!focused) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setFocused(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [focused]);

  useEffect(() => {
    if (focused && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [focused, activeSlot]);

  const activate = (slotId) => {
    if (!focused) return;
    setActiveSlot(slotId);
  };

  const activeKey = PULL_KEYS.find((k) => k.id === activeSlot) || PULL_KEYS[0];

  return (
    <div className="text-[12px] text-ink-muted flex flex-col gap-1" ref={wrapRef}>
      Pull hypothesis
      {!focused ? (
        <button
          type="button"
          onClick={() => {
            setFocused(true);
            setActiveSlot('project');
          }}
          className="text-left px-3 py-2 text-[14px] border border-paper-strong rounded-[4px] bg-white text-ink leading-relaxed hover:bg-wash"
        >
          {sentence}
        </button>
      ) : (
        <div className="border border-paper-strong rounded-[4px] bg-white p-2 space-y-2">
          <p className="px-1 text-[13px] text-ink leading-relaxed">
            There is{' '}
            <span
              className={
                activeSlot === 'project'
                  ? 'bg-callout underline decoration-ink/30'
                  : 'text-ink'
              }
            >
              {values.project}
            </span>{' '}
            that is{' '}
            <span
              className={
                activeSlot === 'unavoidable'
                  ? 'bg-callout underline decoration-ink/30'
                  : 'text-ink'
              }
            >
              {values.unavoidable}
            </span>
            , and{' '}
            <span
              className={
                activeSlot === 'looking'
                  ? 'bg-callout underline decoration-ink/30'
                  : 'text-ink'
              }
            >
              {values.looking}
            </span>{' '}
            are{' '}
            <span
              className={
                activeSlot === 'lacking'
                  ? 'bg-callout underline decoration-ink/30'
                  : 'text-ink'
              }
            >
              {values.lacking}
            </span>
            .
          </p>
          <div className="flex flex-wrap gap-1.5">
            {PULL_KEYS.map((key) => (
              <button
                key={key.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => activate(key.id)}
                className={`px-2 py-0.5 text-[12px] rounded-[3px] ${
                  activeSlot === key.id
                    ? 'bg-wash-active text-ink'
                    : 'bg-callout text-ink-muted hover:bg-wash hover:text-ink'
                }`}
                title={key.hint}
              >
                <span className="font-semibold">{key.letter}</span>
                <span className="ml-1">{key.label}</span>
              </button>
            ))}
          </div>
          <input
            ref={inputRef}
            value={values[activeSlot] || ''}
            onChange={(e) =>
              onChange({
                ...values,
                [activeSlot]: e.target.value,
              })
            }
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                setFocused(false);
              }
            }}
            placeholder={activeKey.hint}
            className="w-full px-2 py-1.5 text-[14px] border border-paper-strong rounded-[4px] bg-white text-ink"
          />
          <div className="flex justify-between px-0.5 text-[12px] text-ink-faint">
            <span>Click a key to edit that part · Esc to done</span>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setFocused(false)}
              className="text-ink-muted hover:text-ink"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const CSV_COLUMN_OPTIONS = [
  { id: 'email', label: 'Email' },
  { id: 'first_name', label: 'First name' },
  { id: 'domain', label: 'Domain' },
  { id: 'product_name', label: 'Product' },
  { id: 'tagline', label: 'Tagline' },
  { id: 'description', label: 'Description' },
  { id: 'votes_count', label: 'Votes', align: 'right' },
  { id: 'ph_url', label: 'Product Hunt' },
  { id: 'website', label: 'Website' },
  { id: 'classification', label: 'Type' },
  { id: 'verification', label: 'Status' },
  { id: 'score', label: 'Score', align: 'right' },
  { id: 'subject', label: 'Subject' },
  { id: 'finished_email', label: 'Finished email' },
  { id: 'pull_hypothesis', label: 'Pull hypothesis' },
];

const CSV_COLUMNS = CSV_COLUMN_OPTIONS.map((c) => c.id);
const COLUMN_BY_ID = Object.fromEntries(CSV_COLUMN_OPTIONS.map((c) => [c.id, c]));

function displayValue(draft, key) {
  if (key === 'finished_email') return draft.finished_email || draft.body || '';
  const value = draft[key];
  return value == null ? '' : value;
}

function DraftCell({ col, draft, first }) {
  const raw = displayValue(draft, col.id);
  const empty = raw === '' || raw == null;
  const title = first || col.id === 'subject' || col.id === 'first_name';
  const max =
    col.id === 'finished_email' || col.id === 'description' || col.id === 'pull_hypothesis'
      ? 'max-w-[22rem]'
      : col.id === 'subject' || col.id === 'tagline'
        ? 'max-w-[18rem]'
        : 'max-w-[14rem]';

  let inner;
  if (empty) {
    inner = <span className="text-ink-faint">—</span>;
  } else if (col.id === 'classification') {
    inner = <Chip tone={classificationTone(raw)}>{raw}</Chip>;
  } else if (col.id === 'verification') {
    inner = <Chip tone={verificationTone(raw)}>{raw}</Chip>;
  } else if (col.id === 'ph_url' || col.id === 'website') {
    inner = (
      <ExternalLink href={raw}>
        <span className={`block truncate ${max}`}>{raw}</span>
      </ExternalLink>
    );
  } else if (col.id === 'votes_count' || col.id === 'score') {
    inner = <span className="tabular-nums">{raw}</span>;
  } else {
    inner = (
      <span className={`block truncate ${max} ${title ? 'text-ink font-medium' : 'text-ink-muted'}`}>
        {String(raw)}
      </span>
    );
  }

  return (
    <Td
      first={first}
      align={col.align}
      className={title && col.id !== 'classification' ? 'text-ink font-medium' : ''}
    >
      {inner}
    </Td>
  );
}

function DownloadCsvSplit({
  count,
  disabled,
  selected,
  onChange,
  onDownload,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const allSelected =
    CSV_COLUMN_OPTIONS.length > 0 &&
    CSV_COLUMN_OPTIONS.every((o) => selected.includes(o.id));

  const toggle = (id) => {
    onChange(
      selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]
    );
  };

  return (
    <div className="relative flex-shrink-0" ref={ref}>
      <div
        className={`inline-flex rounded-[4px] overflow-hidden ${
          disabled ? 'opacity-40' : ''
        }`}
      >
        <button
          type="button"
          onClick={onDownload}
          disabled={disabled}
          className="text-[13px] px-3 py-1.5 bg-cta text-white hover:bg-cta-hover disabled:hover:bg-cta inline-flex items-center"
        >
          Download CSV
          <span className="ml-1.5 text-[12px] text-white/80 tabular-nums">{count}</span>
        </button>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          disabled={disabled}
          className="px-2 py-1.5 bg-cta text-white hover:bg-cta-hover border-l border-white/20 disabled:hover:bg-cta"
          title="Choose CSV columns"
          aria-label="Choose CSV columns"
          aria-expanded={open}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="w-3.5 h-3.5"
          >
            <path
              fillRule="evenodd"
              d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>
      {open && (
        <div className="absolute left-0 top-full mt-1.5 z-50 w-56 max-h-[320px] overflow-y-auto bg-white border border-paper-line rounded-md shadow-popover py-1">
          <div className="px-3 py-1.5 text-[12px] text-ink-faint border-b border-paper-line">
            Columns · {selected.length}/{CSV_COLUMN_OPTIONS.length}
          </div>
          <label className="flex items-center gap-2 px-3 py-1.5 text-[12px] hover:bg-wash cursor-pointer border-b border-paper-line">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={(e) =>
                onChange(e.target.checked ? CSV_COLUMNS.slice() : [])
              }
              className="rounded border-paper-line"
            />
            <span className="font-medium">Select all</span>
          </label>
          {CSV_COLUMN_OPTIONS.map((opt) => (
            <label
              key={opt.id}
              className="flex items-center gap-2 px-3 py-1.5 text-[12px] hover:bg-wash cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selected.includes(opt.id)}
                onChange={() => toggle(opt.id)}
                className="rounded border-paper-line"
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function DraftsView({
  emails = [], // already waterfall-filtered from Contacts
  drafts = [],
  onDraftsChange,
  onHideEmail,
}) {
  const [template, setTemplate] = useState(() => loadDraftTemplate());
  const [templateOpen, setTemplateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [csvColumns, setCsvColumns] = useState(() =>
    loadDraftCsvColumns(CSV_COLUMNS)
  );

  useEffect(() => {
    saveDraftTemplate(template);
  }, [template]);

  useEffect(() => {
    saveDraftCsvColumns(csvColumns);
  }, [csvColumns]);

  const exportColumns = useMemo(
    () => CSV_COLUMNS.filter((c) => csvColumns.includes(c)),
    [csvColumns]
  );

  useEffect(() => {
    if (selectedId && !drafts.some((d) => d.id === selectedId)) {
      setSelectedId(null);
    }
  }, [drafts, selectedId]);

  const selected = useMemo(
    () => drafts.find((d) => d.id === selectedId) || null,
    [drafts, selectedId]
  );

  const generateDrafts = () => {
    if (!emails.length) {
      window.alert('No matching contacts. Widen filters on Contacts.');
      return;
    }

    onDraftsChange(
      syncDraftsFromContacts(emails, [], template, { preserveExisting: false })
    );
    setTemplateOpen(false);
  };

  const closeTemplate = () => {
    setTemplateOpen(false);
    if (emails.length) generateDrafts();
  };

  const updateSelected = (patch) => {
    if (!selected) return;
    onDraftsChange(
      drafts.map((d) => {
        if (d.id !== selected.id) return d;
        const next = { ...d, ...patch, updatedAt: new Date().toISOString() };
        if (patch.body != null) next.finished_email = patch.body;
        if (patch.finished_email != null) next.body = patch.finished_email;
        return next;
      })
    );
  };

  const removeDraft = (id) => {
    const email = id.split('::')[0];
    if (email) onHideEmail?.(email);
    if (selectedId === id) setSelectedId(null);
  };

  const handleDownload = () => {
    if (!exportColumns.length) {
      window.alert('Select at least one CSV column.');
      return;
    }
    downloadCsv(
      `outreach-drafts-${new Date().toISOString().slice(0, 10)}.csv`,
      exportColumns,
      drafts.map((d) => exportColumns.map((key) => d[key] ?? ''))
    );
  };

  const handleClear = () => {
    if (!window.confirm('Clear all emails?')) return;
    clearDrafts();
    onDraftsChange([]);
    setSelectedId(null);
  };

  const insertWildcard = (key) => {
    const token = `{{${key}}}`;
    setTemplate((t) => ({
      ...t,
      body: `${t.body}${t.body.endsWith(' ') || !t.body ? '' : ' '}${token}`,
    }));
  };

  const generateDraftsRef = useRef(generateDrafts);
  generateDraftsRef.current = generateDrafts;

  useEffect(() => {
    const offApply = onCommand('apply_draft_template', () =>
      generateDraftsRef.current?.()
    );
    const offTemplate = onCommand('set_draft_template', (patch) => {
      if (!patch) return;
      setTemplate((t) => ({
        ...t,
        ...(patch.subject != null ? { subject: patch.subject } : {}),
        ...(patch.body != null ? { body: patch.body } : {}),
      }));
    });
    const offPull = onCommand('set_pull_hypothesis', (pull) => {
      if (!pull) return;
      setTemplate((t) => ({
        ...t,
        pull: { ...DEFAULT_PULL, ...(t.pull || {}), ...pull },
      }));
    });
    return () => {
      offApply();
      offTemplate();
      offPull();
    };
  }, []);

  return (
    <div className="h-full flex flex-col min-w-0">
      <header className="px-8 pt-8 pb-4 border-b border-paper-line">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-[40px] font-bold tracking-tight text-ink leading-[1.15]">
              Emails
            </h1>
            <p className="text-[14px] text-ink-muted mt-1.5 max-w-2xl">
              One email per contact — always in sync with Contacts. Edit the template to rewrite all emails.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => setTemplateOpen(true)}
              className="text-[13px] px-3 py-1.5 rounded-[4px] border border-paper-strong bg-white hover:bg-wash"
            >
              Edit template
            </button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <DownloadCsvSplit
            count={drafts.length}
            disabled={drafts.length === 0 || exportColumns.length === 0}
            selected={csvColumns}
            onChange={setCsvColumns}
            onDownload={handleDownload}
          />
          <button
            type="button"
            onClick={handleClear}
            disabled={drafts.length === 0}
            className="text-[13px] text-ink-muted hover:text-ink disabled:opacity-40"
          >
            Clear emails
          </button>
          <span className="text-[12px] text-ink-faint tabular-nums">
            {drafts.length} emails · {emails.length} contacts
          </span>
        </div>
      </header>

      <DataTable
        columns={[
          ...exportColumns.map((key, i) => {
            const col = COLUMN_BY_ID[key];
            return {
              key,
              label: col?.label || key,
              first: i === 0,
              align: col?.align,
            };
          }),
          { key: 'actions', label: '', last: true, align: 'right', width: '4.5rem' },
        ]}
        empty={
          drafts.length === 0
            ? emails.length > 0
              ? 'No emails match the current contact filters. Change filters on Contacts.'
              : 'Pull contacts first — emails appear here automatically, one per contact.'
            : null
        }
      >
        {drafts.map((d) => (
          <Tr
            key={d.id}
            active={selectedId === d.id}
            onClick={() => setSelectedId(d.id)}
          >
            {exportColumns.map((key, i) => (
              <DraftCell
                key={key}
                col={COLUMN_BY_ID[key] || { id: key }}
                draft={d}
                first={i === 0}
              />
            ))}
            <TdAction>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeDraft(d.id);
                }}
                className="text-[12px] text-ink-muted hover:text-ink"
              >
                Remove
              </button>
            </TdAction>
          </Tr>
        ))}
      </DataTable>

      <Modal
        open={templateOpen}
        onClose={closeTemplate}
        title="Edit template"
        subtitle="Done rewrites emails from the contacts visible on Contacts."
        titleId="email-template-title"
        maxWidth="max-w-lg"
        footer={
          <button
            type="button"
            onClick={closeTemplate}
            className="text-[13px] px-3 py-1.5 rounded-[4px] bg-cta text-white hover:bg-cta-hover"
          >
            Done
          </button>
        }
      >
        <div className="space-y-3">
          <PullHypothesisField
            pull={template.pull}
            onChange={(pull) => setTemplate((t) => ({ ...t, pull }))}
          />
          <label className="text-[12px] text-ink-muted flex flex-col gap-1">
            Subject template
            <input
              value={template.subject}
              onChange={(e) => setTemplate((t) => ({ ...t, subject: e.target.value }))}
              className="px-3 py-2 text-[14px] border border-paper-strong rounded-[4px] bg-white text-ink"
              placeholder="Loved {{product_name}} on Product Hunt"
            />
          </label>
          <label className="text-[12px] text-ink-muted flex flex-col gap-1">
            Email body template
            <textarea
              value={template.body}
              onChange={(e) => setTemplate((t) => ({ ...t, body: e.target.value }))}
              rows={8}
              className="px-3 py-2 text-[14px] border border-paper-strong rounded-[4px] bg-white text-ink leading-relaxed resize-y"
              placeholder="Hey {{first_name}}, saw {{product_name}}…"
            />
          </label>
          <div className="flex flex-wrap gap-1.5">
            {WILDCARDS.map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => insertWildcard(key)}
                className="px-1.5 py-0.5 text-[12px] rounded-[3px] bg-callout text-ink-muted hover:text-ink hover:bg-wash"
                title={`Insert {{${key}}}`}
              >
                {`{{${key}}}`}
              </button>
            ))}
          </div>
        </div>
      </Modal>

      <Modal
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        title="Finished email"
        subtitle={
          selected ? (
            <Lineage
              domain={selected.domain}
              contact={selected.email}
              website={selected.website}
            />
          ) : null
        }
        titleId="finished-email-title"
        maxWidth="max-w-xl"
        footer={
          <button
            type="button"
            onClick={() => setSelectedId(null)}
            className="text-[13px] px-3 py-1.5 rounded-[4px] border border-paper-strong bg-white hover:bg-wash"
          >
            Done
          </button>
        }
      >
        {selected ? (
          <div className="space-y-4">
            <div className="text-[12px] text-ink-faint space-y-1">
              <div>{selected.product_name || '—'}</div>
              {selected.tagline ? (
                <div className="italic text-ink-muted">“{selected.tagline}”</div>
              ) : null}
            </div>
            <label className="text-[12px] text-ink-muted flex flex-col gap-1">
              Subject
              <input
                value={selected.subject}
                onChange={(e) => updateSelected({ subject: e.target.value })}
                className="px-3 py-2 text-[14px] border border-paper-strong rounded-[4px] bg-white text-ink"
              />
            </label>
            <label className="text-[12px] text-ink-muted flex flex-col gap-1">
              Finished email
              <textarea
                value={selected.finished_email || selected.body || ''}
                onChange={(e) =>
                  updateSelected({
                    finished_email: e.target.value,
                    body: e.target.value,
                  })
                }
                rows={14}
                className="px-3 py-2 text-[14px] border border-paper-strong rounded-[4px] bg-white text-ink leading-relaxed resize-y"
              />
            </label>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

export default DraftsView;
